import { useEffect, useState } from 'react';
import { usersApi, canGrantPresales, type SessionUser, type UserPatch } from './api';

/**
 * "Manage Users" — list, create, edit and deactivate accounts.
 *
 * Reachable only by a superuser or an account with "Users Admin". The server
 * enforces that on every endpoint; this component hiding the button is a
 * convenience, not the control.
 *
 * Accounts are never deleted, only deactivated. A user is referenced by the
 * clients and deals they created, and those references have to keep resolving
 * long after someone leaves.
 */

interface Props {
  onClose: () => void;
  /** The signed-in user. Only a superuser may grant superuser. */
  currentUser: SessionUser;
}

const PAGE_SIZE = 20;
const MIN_PASSWORD = 12;

const input: React.CSSProperties = {
  width: '100%', padding: '8px 10px', fontSize: '13px', boxSizing: 'border-box',
  border: '1px solid #E5E7EB', borderRadius: '6px', outline: 'none',
  fontFamily: 'inherit', background: 'white', color: '#1A1A1A',
};
const label: React.CSSProperties = {
  display: 'block', fontSize: '12px', fontWeight: 500, color: '#374151', margin: '0 0 4px',
};
const btn = (kind: 'primary' | 'plain' | 'danger'): React.CSSProperties => ({
  padding: '6px 12px', fontSize: '12px', fontWeight: 500, borderRadius: '4px',
  cursor: 'pointer', fontFamily: 'inherit',
  background: kind === 'primary' ? '#000E9C' : 'white',
  color: kind === 'primary' ? 'white' : kind === 'danger' ? '#B00020' : '#000E9C',
  border: kind === 'primary' ? 'none' : `1px solid ${kind === 'danger' ? '#FECACA' : '#000E9C'}`,
});

type Draft = {
  id: string | null;
  username: string;
  fullName: string;
  email: string;
  password: string;
  isUsersAdmin: boolean;
  isPresales: boolean;
  isSuperuser: boolean;
  isActive: boolean;
};

const emptyDraft = (): Draft => ({
  id: null, username: '', fullName: '', email: '', password: '',
  isUsersAdmin: false, isPresales: false, isSuperuser: false, isActive: true,
});

const draftOf = (u: SessionUser): Draft => ({
  id: u.id, username: u.username, fullName: u.fullName ?? '', email: u.email ?? '',
  password: '', isUsersAdmin: u.isUsersAdmin, isPresales: u.isPresales,
  isSuperuser: u.isSuperuser, isActive: u.isActive,
});

export function ManageUsers({ onClose, currentUser }: Props) {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [data, setData] = useState<{ users: SessionUser[]; pageCount: number; total: number }>({
    users: [], pageCount: 1, total: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [reload, setReload] = useState(0);
  const mayGrantPresales = canGrantPresales(currentUser);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      usersApi
        .list(page, search, PAGE_SIZE)
        .then((r) => { if (!cancelled) { setData(r); setError(null); } })
        .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [page, search, reload]);

  const save = async () => {
    if (!draft || saving) return;
    setSaving(true);
    setError(null);
    try {
      if (draft.id === null) {
        if (draft.password.length < MIN_PASSWORD) {
          throw new Error(`Password must be at least ${MIN_PASSWORD} characters.`);
        }
        await usersApi.create({
          username: draft.username, password: draft.password,
          fullName: draft.fullName, email: draft.email,
          isUsersAdmin: draft.isUsersAdmin, isSuperuser: draft.isSuperuser,
          ...(mayGrantPresales ? { isPresales: draft.isPresales } : {}),
        });
      } else {
        const patch: UserPatch = {
          fullName: draft.fullName, email: draft.email,
          isUsersAdmin: draft.isUsersAdmin, isActive: draft.isActive,
        };
        if (mayGrantPresales) patch.isPresales = draft.isPresales;
        if (currentUser.isSuperuser) patch.isSuperuser = draft.isSuperuser;
        // Blank means "leave the password alone".
        if (draft.password) patch.password = draft.password;
        await usersApi.update(draft.id, patch);
      }
      setDraft(null);
      setReload((n) => n + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (u: SessionUser) => {
    const verb = u.isActive ? 'Deactivate' : 'Reactivate';
    if (!window.confirm(`${verb} ${u.username}?`)) return;
    setError(null);
    try {
      await usersApi.update(u.id, { isActive: !u.isActive });
      setReload((n) => n + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 110, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '40px 16px', overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#F9FAFB', borderRadius: '10px', width: '100%', maxWidth: '900px',
          padding: '24px', boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <h2 style={{ margin: 0, fontSize: '18px', color: '#1A1A1A', flex: 1 }}>Manage users</h2>
          {!draft && (
            <button type="button" style={btn('primary')} onClick={() => setDraft(emptyDraft())}>
              New user
            </button>
          )}
          <button
            type="button" onClick={onClose} aria-label="Close"
            style={{ background: 'none', border: 'none', fontSize: '22px', color: '#6B7280', cursor: 'pointer', lineHeight: 1 }}
          >
            ×
          </button>
        </div>

        {error && (
          <div style={{ padding: '10px 12px', marginBottom: '12px', borderRadius: '6px', background: '#FEF2F2', color: '#B00020', fontSize: '13px' }}>
            {error}
          </div>
        )}

        {draft ? (
          <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
            <h3 style={{ margin: '0 0 14px', fontSize: '15px', color: '#1A1A1A' }}>
              {draft.id === null ? 'New user' : `Edit ${draft.username}`}
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={label}>Username</label>
                <input
                  style={{ ...input, background: draft.id === null ? 'white' : '#F3F4F6' }}
                  value={draft.username}
                  disabled={draft.id !== null}
                  onChange={(e) => setDraft({ ...draft, username: e.target.value })}
                  autoCapitalize="none" spellCheck={false}
                />
              </div>
              <div>
                <label style={label}>Full name</label>
                <input
                  style={input}
                  value={draft.fullName}
                  onChange={(e) => setDraft({ ...draft, fullName: e.target.value })}
                  placeholder="Pre-fills “Your name” on the quote"
                />
              </div>
              <div>
                <label style={label}>Email</label>
                <input
                  style={input} type="email" value={draft.email}
                  onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                />
              </div>
              <div>
                <label style={label}>
                  {draft.id === null ? 'Password' : 'New password (blank keeps the current one)'}
                </label>
                <input
                  style={input} type="password" value={draft.password}
                  autoComplete="new-password"
                  onChange={(e) => setDraft({ ...draft, password: e.target.value })}
                  placeholder={`At least ${MIN_PASSWORD} characters`}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '18px', alignItems: 'center', margin: '14px 0 4px', flexWrap: 'wrap' }}>
              <label style={{ fontSize: '13px', color: '#374151', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <input
                  type="checkbox" checked={draft.isUsersAdmin}
                  onChange={(e) => setDraft({ ...draft, isUsersAdmin: e.target.checked })}
                />
                Users Admin
              </label>
              {/* Presales authorises custom discounts on a quote, so the
                  checkbox only exists for someone allowed to hand that out:
                  the superuser, or an account that is itself presales AND a
                  users admin. The server enforces the same rule. */}
              {mayGrantPresales && (
                <label style={{ fontSize: '13px', color: '#374151', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <input
                    type="checkbox" checked={draft.isPresales}
                    onChange={(e) => setDraft({ ...draft, isPresales: e.target.checked })}
                  />
                  Presales
                </label>
              )}
              {/* Only a superuser can create or revoke another superuser. */}
              {currentUser.isSuperuser && (
                <label style={{ fontSize: '13px', color: '#374151', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <input
                    type="checkbox" checked={draft.isSuperuser}
                    onChange={(e) => setDraft({ ...draft, isSuperuser: e.target.checked })}
                  />
                  Superuser
                </label>
              )}
              {draft.id !== null && (
                <label style={{ fontSize: '13px', color: '#374151', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <input
                    type="checkbox" checked={draft.isActive}
                    onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
                  />
                  Active
                </label>
              )}
            </div>

            <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
              <button type="button" style={btn('primary')} onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button type="button" style={btn('plain')} onClick={() => { setDraft(null); setError(null); }}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <input
            style={{ ...input, marginBottom: '14px', padding: '10px 12px', fontSize: '14px' }}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Filter by username, full name or email…"
          />
        )}

        {loading && <p style={{ color: '#6B7280', fontSize: '14px' }}>Loading…</p>}

        {!loading && (
          <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#6B7280', background: '#F9FAFB' }}>
                  <th style={{ padding: '8px 12px', fontWeight: 500 }}>Username</th>
                  <th style={{ padding: '8px 12px', fontWeight: 500 }}>Full name</th>
                  <th style={{ padding: '8px 12px', fontWeight: 500 }}>Role</th>
                  <th style={{ padding: '8px 12px', fontWeight: 500 }}>Status</th>
                  <th style={{ padding: '8px 12px' }} />
                </tr>
              </thead>
              <tbody>
                {data.users.map((u) => (
                  <tr key={u.id} style={{ borderTop: '1px solid #F3F4F6', opacity: u.isActive ? 1 : 0.55 }}>
                    <td style={{ padding: '8px 12px', color: '#1A1A1A' }}>{u.username}</td>
                    <td style={{ padding: '8px 12px', color: '#6B7280' }}>{u.fullName || '—'}</td>
                    <td style={{ padding: '8px 12px', color: '#6B7280' }}>
                      {[
                        u.isSuperuser ? 'Superuser' : u.isUsersAdmin ? 'Users Admin' : 'User',
                        u.isPresales ? 'Presales' : null,
                      ].filter(Boolean).join(' · ')}
                    </td>
                    <td style={{ padding: '8px 12px', color: u.isActive ? '#047857' : '#B00020' }}>
                      {u.isActive ? 'Active' : 'Inactive'}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button type="button" style={{ ...btn('plain'), marginRight: '6px' }} onClick={() => setDraft(draftOf(u))}>
                        Edit
                      </button>
                      <button
                        type="button"
                        style={btn(u.isActive ? 'danger' : 'plain')}
                        onClick={() => toggleActive(u)}
                        // Deactivating yourself would end your own session; the
                        // server refuses it too.
                        disabled={u.id === currentUser.id && u.isActive}
                        title={u.id === currentUser.id && u.isActive ? 'You cannot deactivate your own account' : ''}
                      >
                        {u.isActive ? 'Deactivate' : 'Reactivate'}
                      </button>
                    </td>
                  </tr>
                ))}
                {data.users.length === 0 && (
                  <tr><td colSpan={5} style={{ padding: '16px 12px', color: '#6B7280' }}>No user matches that filter.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {data.pageCount > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '12px' }}>
            <button type="button" style={btn('plain')} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Previous
            </button>
            <span style={{ fontSize: '13px', color: '#6B7280' }}>
              Page {page} of {data.pageCount} · {data.total} users
            </span>
            <button type="button" style={btn('plain')} disabled={page >= data.pageCount} onClick={() => setPage((p) => p + 1)}>
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
